import { styled } from '@mui/material';
import { GridColDef, GridRowParams, GridRowsProp } from '@mui/x-data-grid-pro';
import { DataGridPro } from '../../tables/DataGridPro';
import React, { useCallback, useMemo } from 'react';
import { isEqual } from 'lodash';
import { TransportationAttributesGroupCell } from './TransportationAttributesGroupCell';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

export interface TransportationAttributesGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  handleRowClick: (transportationAttributeUUID: string) => void;
  isLoading?: boolean;
}

interface StyledDataGridProProps {
  isEmpty?: boolean;
}

const PREFIX = 'TransportationAttributesGrid';

const StyledDataGridPro = styled(DataGridPro)<StyledDataGridProProps>(
  ({ theme, isEmpty }) => ({
    cursor: 'pointer',
    '& .MuiDataGrid-virtualScrollerContent': {
      width: '100% !important',
      maxWidth: '100% !important',
      height: isEmpty ? '100px !important' : 'auto',
    },
    '.MuiDataGrid-cell.categoryCell': {
      paddingLeft: 10,
    },
    '.MuiDataGrid-columnHeader.statusHeader': {
      '.MuiDataGrid-columnHeaderTitleContainerContent': {
        width: '100%',
        justifyContent: 'center',
      },
    },
  }),
);

const TransportationAttributesGrid = ({
  rows,
  columns,
  handleRowClick,
  isLoading,
}: TransportationAttributesGridProps) => {
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const getTreeDataPath = useCallback((row: any) => row.hierarchy, []);

  const groupingColDef = useMemo(
    () => ({
      hideDescendantCount: true,
      headerName: t('transportationAttributes.name'),
      renderCell: TransportationAttributesGroupCell,
      minWidth: 300,
    }),
    [],
  );

  const onRowClick = (params: GridRowParams) => {
    const uuid = params.row.id.split('#')[0];
    handleRowClick(uuid);
  };

  return (
    <StyledDataGridPro
      rows={rows}
      columns={columns}
      treeData
      getTreeDataPath={getTreeDataPath}
      defaultGroupingExpansionDepth={0}
      groupingColDef={groupingColDef}
      loading={isLoading}
      isEmpty={!rows || rows.length === 0}
      onRowClick={onRowClick}
      disableColumnMenu={true}
      pagination
    />
  );
};

// Memoize to prevent tree from resetting state after rerender
export default React.memo(
  TransportationAttributesGrid,
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.rows, nextProps.rows) &&
      prevProps.isLoading === nextProps.isLoading
    );
  },
);
